import { ClearRounded, ExpandMoreRounded } from "@mui/icons-material";
import {
  alpha,
  AutocompleteProps,
  CircularProgress,
  ListSubheader,
  Autocomplete as MuiAutocomplete,
  styled,
  Typography,
} from "@mui/material";
import { createContext, forwardRef, useContext } from "react";
import { FixedSizeList, ListChildComponentProps } from "react-window";

const StyledMuiAutocomplete = styled(MuiAutocomplete)`
  .MuiInputLabel-root {
    color: ${({ theme }) => theme.palette.grey[400]};

    &.Mui-disabled {
      color: ${({ theme }) => theme.palette.text.disabled};
    }
  }

  .MuiAutocomplete-inputRoot {
    min-height: 48px;
    padding: 6px 12px;
  }

  .MuiAutocomplete-endAdornment .MuiSvgIcon-root {
    color: ${({ theme }) => theme.palette.text.primary};
  }

  .MuiChip-root {
    height: 28px;
    background-color: ${({ theme }) => theme.palette.primary.opacity10};
    color: ${({ theme }) => theme.palette.primary.main};

    .MuiChip-label {
      font-size: ${({ theme }) => theme.typography.body1.fontSize};
      text-transform: none;
    }
  }

  .MuiChip-deleteIcon {
    color: ${({ theme }) => alpha(theme.palette.primary.main, 0.6)};
    font-size: 16px;
    &:hover {
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }
` as typeof MuiAutocomplete;

const LISTBOX_PADDING = 10; // px

interface IProps<T> {
  getOptionLabel: (item: T) => string;
  isLoading?: boolean;
}

const WindowedAutocomplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: IProps<T> & AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
) => {
  const { isLoading, ...other } = props;

  const renderRow = ({ data, index, style }: ListChildComponentProps) => {
    const dataSet = data[index];
    const inlineStyle = {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    };

    if (dataSet.hasOwnProperty("group")) {
      return (
        <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
          {dataSet.group}
        </ListSubheader>
      );
    }

    const { key, ...optionProps } = dataSet[0];
    const optionLabel = props.getOptionLabel(dataSet[1]);

    return (
      <Typography key={key} component="li" {...optionProps} noWrap style={inlineStyle}>
        {optionLabel}
      </Typography>
    );
  };

  const OuterElementContext = createContext({});

  const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
  });

  const ListBoxComponent = forwardRef((props: any, ref: any) => {
    const { children, ...other } = props;
    const itemData: any = [];
    children.forEach((item: any) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    });

    const itemCount = itemData.length;
    const itemSize: number = 36;

    const getChildSize = () => {
      return itemSize;
    };

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }
      return itemData.map(getChildSize).reduce((a: number, b: number) => a + b, 0);
    };

    return (
      <div ref={ref} {...other}>
        <OuterElementContext.Provider value={other}>
          <FixedSizeList
            height={getHeight() + 2 * LISTBOX_PADDING}
            width="100%"
            itemData={itemData}
            itemCount={itemCount}
            itemSize={itemSize}
            outerElementType={OuterElementType}
            innerElementType="ul"
            overscanCount={5}
          >
            {renderRow}
          </FixedSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  });

  return (
    <StyledMuiAutocomplete
      blurOnSelect
      handleHomeEndKeys
      disableListWrap
      clearIcon={<ClearRounded sx={{ fontSize: "20px" }} />}
      ListboxComponent={ListBoxComponent}
      renderOption={(props, option, state) => [props, option, state.index] as React.ReactNode} // We unpack this object in the react-window FixedSizeList component to give us access to "option"
      renderGroup={(params) => params as any}
      popupIcon={
        isLoading ? (
          <CircularProgress size={20} sx={{ color: "text.disabled" }} />
        ) : props.disabled ? null : (
          <ExpandMoreRounded sx={{ color: "primary.main", fontSize: "24px" }} />
        )
      }
      {...other}
    />
  );
};

export { WindowedAutocomplete };
